'use client';

import { useEffect } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { ControlledInput } from 'src/components';
import { faPlus, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { Typography, PulseIconButton } from 'src/components';


interface DynamicSelectOptionsFormProps {
  name: string;
  required?: boolean;
  rules?: any;
}

const DynamicSelectOptionsForm = ({
  ...props
}: DynamicSelectOptionsFormProps) => {
  const {
    control,
    watch,
    formState: { isValid, errors },
    setError,
    clearErrors,
  } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `${props?.name}`,
  });

  const required = props?.required || props.rules?.required;
  useEffect(() => {
    if (watch(`${props?.name}`)?.length === 0) {
      append({ value: '', label: '' });
    }
  }, []);

  function checkForDuplicates() {
    // check both value and label for duplicates
    const values = watch(`${props?.name}`).map((item) => item.value);
    const labels = watch(`${props?.name}`).map((item) => item.label);
    const hasDuplicates =
      new Set(values).size !== values.length ||
      new Set(labels).size !== labels.length;

    if (hasDuplicates) {
      setError('options', {
        message: 'Duplicate values/labels are not allowed',
      });
      return false;
    }

    if (errors.options) clearErrors('options');
    return true;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Typography variant="caption" className="text-gray-500">
          Add values to the select field
        </Typography>
        <PulseIconButton
          onClick={() => {
            // set the current index to the one just added
            append({ value: '', label: '' });
          }}
          size="sm"
          color="success"
          icon={faPlus}
          disabled={!isValid}
          aria-label="Add a Value"
          tooltip="Add a Value"
        />
      </div>
      <div className="flex flex-col" role="list" aria-label="Select options">
        {fields?.map((field, index) => (
          <div
            key={field.id}
            className="flex gap-2 [&:not(:last-child)]:mb-2"
            role="listitem"
            data-testid={`select-option-${index}`}
          >
            <ControlledInput
              autoFocus
              name={`${props?.name}[${index}].value`}
              placeholder="Value..."
              rules={{
                required: required,
                validate: () => checkForDuplicates(),
              }}
              variant="standard"
              size="small"
              label="Value"
              onBlur={() => {
                const value = watch(`${props?.name}[${index}].value`);
                if (!value) {
                  remove(index);
                }
              }}
            />
            <ControlledInput
              name={`${props?.name}[${index}].label`}
              placeholder="Label..."
              rules={{
                required: required,
                validate: () => checkForDuplicates(),
              }}
              variant="standard"
              size="small"
              label="Label"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (checkForDuplicates()) {
                    append({ value: '', label: '' });
                  }
                }
              }}
              onBlur={() => {
                const value = watch(`${props?.name}[${index}].value`);
                const label = watch(`${props?.name}[${index}].label`);

                if (!label && !value) {
                  remove(index);
                }
              }}
            />
            <PulseIconButton
              onClick={() => {
                checkForDuplicates();
                remove(index);
              }}
              color="danger"
              size="sm"
              icon={faTimes}
              aria-label="Remove Value"
              tooltip="Remove Value"
            />
          </div>
        ))}
      </div>
      {((!!fields.length && watch(`${props?.name}`)?.length === 0) || !!errors['options']) && (
        <p className="text-red-500 text-xs mt-1">
          {!!fields.length && watch(`${props?.name}`)?.length === 0
            ? 'At least one value is required'
            : ''}
          {!!errors['options'] && errors['options']?.message?.toString()}
        </p>
      )}
    </div>
  );
};

export default DynamicSelectOptionsForm;
